/**
 * The top bar of docs/07-gui-proposal §2 — what belongs to the PAGE: the global period, the
 * data-freshness badge (always on screen — old numbers must not look new), light/dark, and the
 * offer to install the app.
 *
 * Identity (mark, name, account) left this bar on 12/09/2026 for the full-height sidebar
 * (docs/13-tasks/01-ui-chrome.md#18 §2): it answers "where am I", the same question as the navigation, and on the
 * top bar it was crowding the two controls a reader touches on every single screen.
 *
 * ≤ 900 px the bar grows one more control at the very front: ☰, which opens the navigation
 * drawer (docs/13-tasks/01-ui-chrome.md#16 M1). It is FIRST in the source order so the keyboard and a screen reader
 * reach the menu before the page, and `responsive.css` is what makes it appear at all.
 */
import { Link } from 'react-router';

import { useInstallPrompt } from '../hooks/useInstallPrompt.js';
import { useTheme } from '../hooks/useTheme.js';
import { INSTALL_LABEL } from '../utils/installApp.js';
import { THEME_LABELS } from '../utils/theme.js';
import { FreshnessBadge } from './FreshnessBadge.js';
import { PeriodPicker } from './PeriodPicker.js';

export function TopBar({ navOpen, onToggleNav }: { navOpen: boolean; onToggleNav: () => void }) {
  const { theme, toggle } = useTheme();
  const { canInstall, install } = useInstallPrompt();

  return (
    <header className="topbar">
      <button
        type="button"
        className="navtoggle"
        aria-expanded={navOpen}
        aria-controls="mainnav"
        aria-label={navOpen ? 'Đóng điều hướng' : 'Mở điều hướng'}
        onClick={onToggleNav}
      >
        <span aria-hidden="true">{navOpen ? '✕' : '☰'}</span>
      </button>

      <PeriodPicker />
      <FreshnessBadge />

      {/* Đẩy cụm nút phải sang lề phải — chỗ này trước kia là `margin-right: auto` của `.brand`. */}
      <span className="topgap" aria-hidden="true" />

      {/*
        Lối vào Live Tracking Vessel (docs/14-vessel-map). Là một `Link` chứ không phải `button`:
        nó ĐIỀU HƯỚNG, nên phải mở được ở tab mới, phải có địa chỉ khi rê chuột, và bàn phím phải
        đọc nó là liên kết.

        Chấm nhấp nháy nói "đang theo dõi". Nó là dấu hiệu DUY NHẤT phân biệt nút này với các nút
        còn lại, nên ở chế độ tắt hiệu ứng chuyển động nó không được biến mất — `visuals.css` giữ
        chấm, chỉ bỏ nhịp nháy.
      */}
      <Link to="/map" className="mapbtn" title="Live Tracking Vessel — bản đồ điều động theo ngày">
        <span className="livedot" aria-hidden="true" />
        MAP
      </Link>

      {/*
        The label is fixed, as in the mockup — the button says what it does, not what state it is
        in. Which state that is stays readable in the tooltip, so the three-way cycle
        (tối → sáng → theo hệ thống) loses nothing.
      */}
      <button
        type="button"
        onClick={toggle}
        className="iconbtn"
        title={`Giao diện: ${THEME_LABELS[theme]} — bấm để đổi`}
      >
        Sáng / tối
      </button>

      {/*
        Chỉ hiện khi trình duyệt đã mời cài và trang chưa chạy trong khung ứng dụng — Safari
        không bao giờ mời, ở đó người dùng cài bằng "Chia sẻ → Thêm vào Màn hình chính".
      */}
      {canInstall && (
        <button
          type="button"
          onClick={install}
          className="iconbtn"
          title={`${INSTALL_LABEL} TT Vessels vào máy`}
        >
          {INSTALL_LABEL}
        </button>
      )}
    </header>
  );
}
